import { MutableRefObject, useEffect, useRef } from "react";
import { onSocketMessage } from "../utils/socketRequest";
import { noop } from "../utils/misc";

type SocketHandlerSignature<T> = (data: T) => void;

/**
 * Listen for unsolicited WebSocket messages (server-initiated)
 * Only triggers for messages sent outside of promise-based requests
 *
 * Replaces the old useNuiEvent hook with WebSocket-based communication
 *
 * @param action - The message type to listen for (e.g., "SET_HIDDEN", "SET_COLOR")
 * @param handler - Callback function to handle the message data
 *
 * @example
 * useSocketEventOutsideRequest("SET_HIDDEN", (data: { hide: boolean }) => {
 *   dispatch(SET_hidden(data.hide));
 * });
 *
 * @example
 * useSocketEventOutsideRequest("notification", (data) => {
 *   console.log("New notification:", data);
 *   showToast(data.message);
 * });
 */
export const useSocketEventOutsideRequest = <T = unknown>(
  action: string,
  handler: (data: T) => void,
) => {
  const savedHandler: MutableRefObject<SocketHandlerSignature<T>> = useRef(noop);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    const unsubscribe = onSocketMessage(action, (data: any) => {
      // Filter out messages that are responses to socketRequest (have requestId)
      // useSocketEventOutsideRequest should only handle unsolicited server messages
      if (data.requestId) {
        console.log(`[useSocketEventOutsideRequest] Ignoring response message for action "${action}" (requestId: ${data.requestId})`);
        return;
      }

      if (savedHandler.current) {
        console.log(`[useSocketEventOutsideRequest] Received unsolicited message for action "${action}":`, data);
        savedHandler.current(data as T);
      }
    });

    return () => unsubscribe();
  }, [action]);
};
